import ordersApi from 'api/requests/orders';
import Icon from 'components/_UI/Icon';
import Loader from 'components/_UI/Loader';
import currency from 'helpers/currency';
import React, { useEffect, useState } from 'react'
import { CreditCard, User, Calendar, Package, Hash, Mail } from 'lucide-react';
import styled from 'styled-components'
import { Column } from 'styled/alignment/Column'
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import OrderRefund from './OrderRefund';
import RefundRequestCard from './RefundRequestCard';
import { useRefundRequestsOptional } from 'context/refundRequests/RefundRequests';

const STATUS_MAP = {
    approved: { label: 'Aprovado', bg: '#DCFCE7', color: '#166534' },
    completed: { label: 'Concluído', bg: '#DCFCE7', color: '#166534' },
    accepted: { label: 'Aceito', bg: '#DCFCE7', color: '#166534' },
    pending: { label: 'Pendente', bg: '#FEF3C7', color: '#92400E' },
    canceled: { label: 'Expirado', bg: '#FEE2E2', color: '#DC2626' },
    failed: { label: 'Negado', bg: '#FEE2E2', color: '#DC2626' },
    rejected: { label: 'Recusado', bg: '#FEE2E2', color: '#DC2626' },
    refunded: { label: 'Estornado', bg: '#F3F4F6', color: '#374151' },
    chargeback: { label: 'Chargeback', bg: '#FEE2E2', color: '#991B1B' },
};

export default function OrderDetail({ data, onRefunded }) {
    const refundRequests = useRefundRequestsOptional();
    const [loading, setLoading] = useState(false);
    const [content, setContent] = useState(false);

    useEffect(() => {
        getOrderDetail();
    }, [data?.id])

    async function getOrderDetail({ silent = false } = {}) {
        if (!silent) setLoading(true);

        try {
            const { data: response } = await ordersApi.detail(data?.id);
            setContent(response?.data);
        } catch (error) {

        } finally {
            setLoading(false);
        }
    }

    function formatDate(dateStr) {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('pt-BR');
    }

    function formatTime(dateStr) {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }

    // O estorno muda o status do pedido e o lugar dele na lista: recarrega o
    // detalhe sem piscar o loader e avisa a lista para se atualizar.
    async function handleRefunded() {
        await getOrderDetail({ silent: true });
        onRefunded?.();
    }

    // Respondido o pedido de estorno, o banner e os contadores do painel caem na hora.
    async function handleRefundRequestAnswered() {
        await handleRefunded();
        refundRequests?.refresh();
    }

    // Ao trocar de pedido o `content` ainda é o do anterior até o detalhe novo
    // chegar: só vale o que for do pedido aberto.
    const loadedOrder = content?.id === data?.id ? content : null;
    const refundRequestPending = loadedOrder?.refund_request?.status === 'pending';
    const statusInfo = STATUS_MAP[loadedOrder?.status || data?.status] || STATUS_MAP.pending;

    // Aberto pela lista, o pedido já traz data e evento; aberto por link (a
    // notificação de estorno), só o id: o detalhe carregado cobre os dois casos.
    const createdAt = loadedOrder?.created_at || data?.created_at;
    const eventName = loadedOrder?.event?.name || data?.event?.name;

    return (
        <Container>
            <Header>
                <HeaderLeft>
                    <OrderId>
                        <Hash size={16} color={colors.night} />
                        Pedido {data?.id}
                    </OrderId>
                    <DateText>
                        <Calendar size={13} color={colors.night} />
                        {formatDate(createdAt)} às {formatTime(createdAt)}
                    </DateText>
                </HeaderLeft>
                <StatusBadge bgColor={statusInfo.bg} textColor={statusInfo.color}>
                    {statusInfo.label}
                </StatusBadge>
            </Header>

            {loading ? (
                <LoaderWrapper>
                    <Loader />
                </LoaderWrapper>
            ) : (
                <>
                    {/* Com um pedido de estorno esperando, a resposta a ele substitui o botão de estornar. */}
                    {refundRequestPending ? (
                        <RefundRequestCard refundRequest={loadedOrder.refund_request} onAnswered={handleRefundRequestAnswered} />
                    ) : (
                        <OrderRefund orderId={data?.id} refund={loadedOrder?.refund} onRefunded={handleRefunded} />
                    )}

                    <CardsGrid>
                        {/* Customer Card */}
                        <Card>
                            <CardIcon>
                                <User size={16} color={colors.primaryLight} />
                            </CardIcon>
                            <CardContent>
                                <CardLabel>Cliente</CardLabel>
                                <CardTitle>{content?.customer?.name}</CardTitle>
                                <CardMeta>
                                    <Mail size={12} color={colors.night} />
                                    {content?.customer?.email}
                                </CardMeta>
                            </CardContent>
                        </Card>

                        {/* Payment Card */}
                        <Card>
                            <CardIcon>
                                {content?.payment_method === 'credit_card' ? (
                                    <CreditCard size={16} color={colors.primaryLight} />
                                ) : (
                                    <Column width='16px' height='16px'>
                                        <Icon name='pix' color={colors.primaryLight} />
                                    </Column>
                                )}
                            </CardIcon>
                            <CardContent>
                                <CardLabel>Pagamento</CardLabel>
                                <CardTitle>
                                    {content?.payment_method === 'credit_card' ? 'Cartão de crédito' : 'PIX'}
                                </CardTitle>
                                {content?.payment_method === 'credit_card' && content?.installments && (
                                    <CardMeta>{content.installments}x parcelas</CardMeta>
                                )}
                            </CardContent>
                        </Card>

                        {/* Event Card */}
                        <Card>
                            <CardIcon>
                                <Calendar size={16} color={colors.primaryLight} />
                            </CardIcon>
                            <CardContent>
                                <CardLabel>Evento</CardLabel>
                                <CardTitle>{eventName}</CardTitle>
                            </CardContent>
                        </Card>
                    </CardsGrid>

                    {/* Value Summary */}
                    <SummaryCard>
                        <SummaryHeader>
                            <SummaryLabel>Resumo do pedido</SummaryLabel>
                            <SummaryTotal>{currency.format(content?.total)}</SummaryTotal>
                        </SummaryHeader>

                        <SummaryRows>
                            <SummaryRow>
                                <SummaryRowLabel>Subtotal dos itens</SummaryRowLabel>
                                <SummaryRowValue>{currency.format(content?.total)}</SummaryRowValue>
                            </SummaryRow>
                            {parseFloat(content?.discount) > 0 && (
                                <SummaryRow>
                                    <SummaryRowLabel>Descontos</SummaryRowLabel>
                                    <SummaryRowValue isNegative>
                                        -{currency.format(content?.discount)}
                                    </SummaryRowValue>
                                </SummaryRow>
                            )}
                            {content?.coupon && (
                                <SummaryRow>
                                    <SummaryRowLabel>Cupom</SummaryRowLabel>
                                    <CouponValue hasCoupon>{content.coupon}</CouponValue>
                                </SummaryRow>
                            )}
                            <SummaryRow>
                                <SummaryRowLabel>Taxa de processamento</SummaryRowLabel>
                                <SummaryRowValue isNegative>
                                    -{currency.format(content?.tax)}
                                </SummaryRowValue>
                            </SummaryRow>
                            <SummaryRow noBorder>
                                <SummaryRowLabel bold>Valor líquido</SummaryRowLabel>
                                <EarningsValue>
                                    {currency.format((content?.total || 0) - (content?.tax || 0))}
                                </EarningsValue>
                            </SummaryRow>
                        </SummaryRows>
                    </SummaryCard>

                    {/* Products */}
                    {content?.products?.length > 0 && (
                        <ProductsSection>
                            <SectionTitle>
                                <Package size={16} color={colors.nightDark} />
                                Itens do pedido ({content.products.length})
                            </SectionTitle>

                            <ProductsTableWrapper>
                            <ProductsTable>
                                <thead>
                                    <tr>
                                        <ProductTh>Produto</ProductTh>
                                        <ProductTh align="center">Qtd</ProductTh>
                                        <ProductTh align="right">Preço</ProductTh>
                                        <ProductTh align="right">Desconto</ProductTh>
                                    </tr>
                                </thead>
                                <tbody>
                                    {content.products.map((item, index) => (
                                        <ProductTr key={index}>
                                            <ProductTd>
                                                <ProductName>{item?.name}</ProductName>
                                            </ProductTd>
                                            <ProductTd align="center">{item?.quantity}</ProductTd>
                                            <ProductTd align="right">{currency.format(item?.price)}</ProductTd>
                                            <ProductTd align="right">
                                                {parseFloat(item?.discount) > 0
                                                    ? <DiscountText>-{currency.format(item?.discount)}</DiscountText>
                                                    : <span style={{ color: colors.night }}>—</span>
                                                }
                                            </ProductTd>
                                        </ProductTr>
                                    ))}
                                </tbody>
                            </ProductsTable>
                        </ProductsTableWrapper>

                            {/* Mobile product cards */}
                            <MobileProducts>
                                {content.products.map((item, index) => (
                                    <MobileProductCard key={index}>
                                        <MobileProductName>{item?.name}</MobileProductName>
                                        <MobileProductDetails>
                                            <MobileProductField>
                                                <MobileProductLabel>Qtd</MobileProductLabel>
                                                <span>{item?.quantity}</span>
                                            </MobileProductField>
                                            <MobileProductField>
                                                <MobileProductLabel>Preço</MobileProductLabel>
                                                <span>{currency.format(item?.price)}</span>
                                            </MobileProductField>
                                            <MobileProductField>
                                                <MobileProductLabel>Desconto</MobileProductLabel>
                                                <span>
                                                    {parseFloat(item?.discount) > 0
                                                        ? <DiscountText>-{currency.format(item?.discount)}</DiscountText>
                                                        : '—'
                                                    }
                                                </span>
                                            </MobileProductField>
                                        </MobileProductDetails>
                                    </MobileProductCard>
                                ))}
                            </MobileProducts>
                        </ProductsSection>
                    )}
                </>
            )}
        </Container>
    )
}

/* ── Layout ────────────────────────────────────── */

const Container = styled(Column)`
    width: 100%;
    gap: 20px;
`;

const LoaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
`;

/* ── Header ────────────────────────────────────── */

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding-bottom: 20px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const HeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const OrderId = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 18px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const DateText = styled.span`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 5px 14px;
    border-radius: 50px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.2px;
    white-space: nowrap;
    background: ${({ bgColor }) => bgColor};
    color: ${({ textColor }) => textColor};
`;

/* ── Info Cards ────────────────────────────────── */

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const Card = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    border-radius: 10px;
    background: ${colors.neutral};
    border: 1px solid ${colors.neutralDark};
`;

const CardIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    min-width: 36px;
    border-radius: 8px;
    background: ${colors.primaryLight}12;
    border: 1px solid ${colors.primaryLight}20;
`;

const CardContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

const CardLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const CardTitle = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const CardMeta = styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/* ── Summary Card ──────────────────────────────── */

const SummaryCard = styled.div`
    border-radius: 10px;
    border: 1px solid ${colors.neutralDark};
    overflow: hidden;
`;

const SummaryHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: ${colors.neutral};
    border-bottom: 1px solid ${colors.neutralDark};
`;

const SummaryLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const SummaryTotal = styled.span`
    font-size: 18px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const SummaryRows = styled.div`
    padding: 4px 20px;
`;

const SummaryRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: ${({ noBorder }) => noBorder ? 'none' : `1px solid ${colors.neutralDark}`};
`;

const SummaryRowLabel = styled.span`
    font-size: 13px;
    color: ${({ bold }) => bold ? colors.nightDark : colors.night};
    font-weight: ${({ bold }) => bold ? 600 : 400};
`;

const SummaryRowValue = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ isNegative }) => isNegative ? colors.danger : colors.nightDark};
`;

const EarningsValue = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.success};
`;

const CouponValue = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ hasCoupon }) => hasCoupon ? colors.primaryLight : colors.night};
`;

/* ── Products Section ──────────────────────────── */

const ProductsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SectionTitle = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const ProductsTableWrapper = styled.div`
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    overflow: hidden;
    display: none;

    @media screen and (min-width: ${breakpoints.md}) {
        display: block;
    }
`;

const ProductsTable = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const ProductTh = styled.th`
    text-align: ${({ align }) => align || 'left'};
    padding: 12px 16px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.3px;
    background: ${colors.neutral};
    border-bottom: 1px solid ${colors.neutralDark};

    &:first-child { padding-left: 20px; }
    &:last-child { padding-right: 20px; }
`;

const ProductTr = styled.tr`
    &:not(:last-child) {
        border-bottom: 1px solid ${colors.neutralDark};
    }
`;

const ProductTd = styled.td`
    padding: 12px 16px;
    font-size: 13px;
    color: ${colors.nightDark};
    vertical-align: middle;
    text-align: ${({ align }) => align || 'left'};

    &:first-child { padding-left: 20px; }
    &:last-child { padding-right: 20px; }
`;

const ProductName = styled.span`
    font-weight: 600;
    color: ${colors.nightDark};
`;

const DiscountText = styled.span`
    color: ${colors.danger};
    font-weight: 500;
`;

/* ── Mobile Products ───────────────────────────── */

const MobileProducts = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    @media screen and (min-width: ${breakpoints.md}) {
        display: none;
    }
`;

const MobileProductCard = styled.div`
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 14px;
`;

const MobileProductName = styled.span`
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const MobileProductDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const MobileProductField = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
    color: ${colors.nightDark};
`;

const MobileProductLabel = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
`;
